import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const InactiveStatus = () => {
  const { checkClientStatus, clientStatus } = useContext(AuthContext);
  const [isChecking, setIsChecking] = useState(false);
  const [pulseAnimation, setPulseAnimation] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Floating animation effect
    const interval = setInterval(() => {
      setPulseAnimation(prev => (prev + 1) % 100);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // If status becomes active, redirect to dashboard
    if (clientStatus === 'active') {
      navigate('/dashboard');
    }
  }, [clientStatus, navigate]);

  const handleRefresh = async () => {
    setIsChecking(true);
    try {
      await checkClientStatus();
      // Status will be checked in the useEffect above
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      // Add a small delay for better UX
      setTimeout(() => {
        setIsChecking(false);
      }, 1000);
    }
  };

  return (
    <div className="inactive-status-page min-vh-100" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      animation: 'gradientBG 15s ease infinite'
    }}>
      <style>
        {`
          @keyframes gradientBG {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px rgba(255, 193, 7, 0.3); }
            50% { box-shadow: 0 0 40px rgba(255, 193, 7, 0.6); }
          }
          @keyframes spinRotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .floating-icon {
            animation: float 6s ease-in-out infinite, glow 2s ease-in-out infinite;
          }
          .status-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border: none;
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
          }
          .status-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
          }
          .refresh-btn {
            background: linear-gradient(45deg, #FF6B6B, #4ECDC4);
            border: none;
            border-radius: 50px;
            padding: 12px 30px;
            font-weight: 600;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }
          .refresh-btn:hover {
            transform: scale(1.05);
            box-shadow: 0 10px 30px rgba(255, 107, 107, 0.3);
          }
          .refresh-btn:active {
            transform: scale(0.95);
          }
          .refresh-btn:disabled {
            cursor: not-allowed;
            opacity: 0.7;
          }
          .logout-btn {
            background: linear-gradient(45deg, #6366f1, #8b5cf6);
            border: none;
            border-radius: 50px;
            padding: 10px 25px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          .logout-btn:hover {
            transform: scale(1.05);
            box-shadow: 0 8px 25px rgba(99, 102, 241, 0.3);
          }
          .contact-item {
            animation: slideInLeft 0.8s ease-out;
            animation-fill-mode: both;
          }
          @keyframes slideInLeft {
            from { transform: translateX(-100px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>

      <div className="container-fluid d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center p-5">
          {/* Animated Main Icon */}
          <div className="mb-5 floating-icon">
            <div style={{
              background: 'linear-gradient(45deg, #FFC107, #FF9800)',
              borderRadius: '50%',
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 10px 30px rgba(255, 193, 7, 0.4)',
              position: 'relative'
            }}>
              <i className="fas fa-exclamation-triangle text-white" style={{
                fontSize: '3.5rem',
                animation: isChecking ? 'spinRotate 1s linear infinite' : 'none'
              }}></i>
              {/* Pulsing rings */}
              <div style={{
                position: 'absolute',
                width: '130px',
                height: '130px',
                border: '2px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '50%',
                top: '-5px',
                left: '-5px',
                animation: 'glow 2s infinite',
                opacity: pulseAnimation > 50 ? 1 : 0.5
              }}></div>
            </div>
          </div>

          {/* Title */}
          <h1 className="display-4 fw-bold text-white mb-3" style={{
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            animation: 'textGlow 2s ease-in-out infinite alternate'
          }}>
            Account Suspended
          </h1>
          <p className="lead text-white-50 mb-5" style={{ fontSize: '1.2rem' }}>
            Your subscription has expired or your account is currently inactive.
          </p>

          {/* Status Card */}
          <div className="status-card mb-5 mx-auto" style={{ maxWidth: '600px' }}>
            <div className="card-body p-4">
              <h5 className="card-title text-warning mb-4" style={{ fontSize: '1.5rem' }}>
                <i className="fas fa-tools me-2"></i>
                To Regain Access:
              </h5>
              <div className="text-start">
                <div className="contact-item mb-3" style={{ animationDelay: '0.2s' }}>
                  <i className="fas fa-check-circle text-success me-3 fs-5"></i>
                  <span className="fw-semibold">Contact our support team to renew your subscription</span>
                </div>
                <div className="contact-item mb-3" style={{ animationDelay: '0.4s' }}>
                  <i className="fas fa-envelope text-info me-3 fs-5"></i>
                  <span>Email: <strong className="text-dark">support@fms.com</strong></span>
                </div>
                <div className="contact-item mb-3" style={{ animationDelay: '0.6s' }}>
                  <i className="fas fa-phone text-primary me-3 fs-5"></i>
                  <span>Phone: <strong className="text-dark">+1 (555) 123-4567</strong></span>
                </div>
                <div className="contact-item mb-2" style={{ animationDelay: '0.8s' }}>
                  <i className="fas fa-clock text-secondary me-3 fs-5"></i>
                  <span>Support Hours: <strong className="text-dark">Monday - Friday, 9 AM - 6 PM EST</strong></span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mb-4">
            <button
              onClick={handleRefresh}
              disabled={isChecking}
              className="btn refresh-btn text-white fw-bold px-4 py-3"
              style={{ minWidth: '200px' }}
            >
              {isChecking ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Checking Status...
                </>
              ) : (
                <>
                  <i className="fas fa-refresh me-2"></i>
                  Check Status Again
                </>
              )}
            </button>

            <Link to="/logout" className="btn logout-btn text-white text-decoration-none px-4 py-3">
              <i className="fas fa-sign-out-alt me-2"></i>
              Logout
            </Link>
          </div>

          {/* Footer Message */}
          <div className="mt-3 text-white-50 small">
            <i className="fas fa-info-circle me-2"></i>
            Please contact support for any questions about your account status.
          </div>
        </div>
      </div>
    </div>
  );
};

export default InactiveStatus;
